import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Send, Upload, Download, Trash2, FileText, CheckCircle2, AlertTriangle, Clock, X, Mail, Copy, Plus } from "lucide-react";
import { MoneyInput } from "@/components/MoneyInput";

function formatBytes(n) {
    if (!n || n <= 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function statusStyle(status) {
    switch (status) {
        case "expired":
            return { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", label: "Expired" };
        case "expiring_soon":
            return { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", label: "Expiring soon" };
        default:
            return { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", label: "Active" };
    }
}

export default function EmployeeDocuments({ employeeId, employeeName, employeeEmail }) {
    const { user } = useAuth();
    const isEmployee = user?.role === "employee";
    const [docs, setDocs] = useState([]);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendLinkOpen, setSendLinkOpen] = useState(false);
    const [sendLinkDefault, setSendLinkDefault] = useState([]);
    const [uploadOpen, setUploadOpen] = useState(false);

    const load = async () => {
        try {
            const [d, t] = await Promise.all([
                api.get(`/documents?employee_id=${employeeId}`),
                api.get(`/documents/types`),
            ]);
            setDocs(d.data || []);
            setTypes(t.data || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load documents");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (employeeId) load();
        // eslint-disable-next-line
    }, [employeeId]);

    const onDownload = async (doc) => {
        try {
            const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.file_name || "document.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
        }
    };

    const onDelete = async (doc) => {
        if (!window.confirm(`Delete "${doc.file_name}"?`)) return;
        try {
            await api.delete(`/documents/${doc.id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    };

    return (
        <div data-testid="employee-documents">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="overline">Compliance & ID</div>
                    <h2 className="mt-2 font-display font-bold text-2xl text-ink">Documents</h2>
                    <p className="text-xs text-ink/50 mt-1">PDFs uploaded by HR or via secure 3-day employee link.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {!isEmployee && (
                    <button
                        onClick={() => setUploadOpen(true)}
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors"
                        data-testid="emp-doc-upload-btn"
                    >
                        <Plus className="w-4 h-4" /> Upload PDF
                    </button>
                    )}
                    {!isEmployee && (
                    <button
                        onClick={() => { setSendLinkDefault(["rent_receipt"]); setSendLinkOpen(true); }}
                        className="inline-flex items-center gap-2 border border-emerald-500 text-emerald-700 px-4 py-2.5 text-sm hover:bg-emerald-50 transition-colors"
                        data-testid="emp-doc-send-rent-link-btn"
                        title="Email a secure 3-day upload link asking the employee for their rent receipt (PAYE tax relief)"
                    >
                        <Send className="w-4 h-4" /> Send rent-receipt link
                    </button>
                    )}
                    {!isEmployee && (
                    <button
                        onClick={() => { setSendLinkDefault([]); setSendLinkOpen(true); }}
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 transition-colors"
                        data-testid="emp-doc-send-link-btn"
                    >
                        <Send className="w-4 h-4" /> Send upload link
                    </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-ink/40">Loading…</div>
            ) : docs.length === 0 ? (
                <div className="bg-white border border-ink/10 p-10 text-center" data-testid="emp-doc-empty">
                    <FileText className="w-10 h-10 text-ink/20 mx-auto mb-3" strokeWidth={1.5} />
                    <div className="text-sm text-ink/60">No documents on file yet</div>
                    <div className="text-xs text-ink/40 mt-1">Click "Send upload link" to email a secure 3-day upload link to {employeeName || "this employee"}.</div>
                </div>
            ) : (
                <div className="bg-white border border-ink/10 overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-ink/50 border-b border-ink/10">
                                <th className="px-4 py-3 font-medium">Document</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3 font-medium">Expires</th>
                                <th className="px-4 py-3 font-medium">Uploaded</th>
                                <th className="px-4 py-3 text-right font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.map((d) => {
                                const st = statusStyle(d.status);
                                return (
                                    <tr key={d.id} className="border-b border-ink/5 hover:bg-bone" data-testid={`emp-doc-row-${d.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="text-ink flex items-center gap-2">
                                                {d.doc_type_label}
                                                {d.approval_status === "pending" && (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 text-[9px] uppercase tracking-[0.15em]" data-testid={`emp-doc-pending-${d.id}`}>
                                                        Pending approval
                                                    </span>
                                                )}
                                                {d.approval_status === "rejected" && (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 border border-red-300 bg-red-50 text-red-800 text-[9px] uppercase tracking-[0.15em]" data-testid={`emp-doc-rejected-${d.id}`}>
                                                        Rejected
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-ink/50 truncate max-w-[260px]" title={d.file_name}>{d.file_name} · {formatBytes(d.file_size)}</div>
                                            {d.doc_type === "rent_receipt" && d.annual_rent_claim > 0 && (
                                                <div className="text-[11px] text-ink/40 mt-0.5">Annual rent claimed: ₦{Number(d.annual_rent_claim).toLocaleString("en-NG")}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center px-2 py-1 border ${st.bg} ${st.border} ${st.text} text-[10px] uppercase tracking-[0.15em]`}>
                                                {st.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-ink/70 tabular-nums">{d.expiry_date?.slice(0, 10) || "—"}</td>
                                        <td className="px-4 py-3 text-xs text-ink/60">
                                            <div>{d.uploaded_at?.slice(0, 10)}</div>
                                            <div className="text-ink/40 truncate max-w-[140px]">by {d.uploaded_by_name}</div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex gap-1">
                                                <button onClick={() => onDownload(d)} className="p-2 hover:bg-ink/5 text-ink/60 hover:text-klein" title="Download" data-testid={`emp-doc-download-${d.id}`}>
                                                    <Download className="w-4 h-4" />
                                                </button>
                                                {!isEmployee && (
                                                <button onClick={() => onDelete(d)} className="p-2 hover:bg-red-50 text-ink/60 hover:text-red-600" title="Delete" data-testid={`emp-doc-delete-${d.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {sendLinkOpen && (
                <SendLinkDialog
                    employeeId={employeeId}
                    employeeName={employeeName}
                    employeeEmail={employeeEmail}
                    types={types}
                    defaultSelected={sendLinkDefault}
                    onClose={() => { setSendLinkOpen(false); setSendLinkDefault([]); }}
                />
            )}
            {uploadOpen && (
                <UploadDialog
                    employeeId={employeeId}
                    types={types}
                    onClose={() => setUploadOpen(false)}
                    onDone={() => { setUploadOpen(false); load(); }}
                />
            )}
        </div>
    );
}

function SendLinkDialog({ employeeId, employeeName, employeeEmail, types, defaultSelected, onClose }) {
    const [selected, setSelected] = useState(defaultSelected || []);
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const toggle = (id) => {
        setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
    };

    const onSend = async () => {
        if (selected.length === 0) {
            toast.error("Pick at least one document type");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post("/documents/upload-token", {
                employee_id: employeeId,
                doc_types: selected,
                note_to_employee: note,
            });
            setResult(data);
            if (data.email_sent) {
                toast.success(`Link sent to ${employeeEmail || employeeName}`);
            } else {
                toast.warning("Link generated but email could not be sent — copy the URL manually");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to generate link");
        } finally {
            setSending(false);
        }
    };

    const uploadUrl = result ? `${window.location.origin}/upload-documents/${result.token}` : "";

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(uploadUrl);
            toast.success("Link copied to clipboard");
        } catch {
            toast.error("Could not copy — please select and copy manually");
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={onClose} data-testid="send-link-dialog">
            <div onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-lg mb-16">
                <div className="px-6 py-5 border-b border-ink/10 flex items-start justify-between">
                    <div>
                        <div className="overline flex items-center gap-2 text-klein"><Mail className="w-3 h-3" /> Document upload</div>
                        <h3 className="mt-1 font-display font-bold text-xl text-ink">Send upload link</h3>
                        <p className="text-xs text-ink/60 mt-1.5">
                            We'll email <strong className="text-ink">{employeeEmail || employeeName}</strong> a secure link that expires in 3 days.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-ink/40 hover:text-ink"><X className="w-5 h-5" /></button>
                </div>

                {result ? (
                    <div className="p-6" data-testid="send-link-result">
                        <div className="flex items-center gap-2 text-emerald-700 mb-4">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="font-medium">{result.email_sent ? "Email delivered" : "Link generated"}</span>
                        </div>
                        <p className="text-sm text-ink/70 mb-3">Share this URL if needed (also sent by email):</p>
                        <div className="flex gap-2">
                            <input value={uploadUrl} readOnly className="flex-1 border border-ink/15 px-3 py-2.5 text-xs font-mono bg-bone" data-testid="send-link-url" />
                            <button onClick={copyLink} className="inline-flex items-center gap-1 px-3 py-2.5 border border-ink/20 text-sm hover:bg-ink hover:text-white" data-testid="send-link-copy">
                                <Copy className="w-3.5 h-3.5" /> Copy
                            </button>
                        </div>
                        <p className="text-xs text-ink/50 mt-3">
                            Expires {new Date(result.expires_at).toLocaleString()}
                        </p>
                        <div className="mt-6 flex justify-end">
                            <button onClick={onClose} className="px-5 py-2.5 bg-ink text-white text-sm hover:bg-ink/90">Done</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="p-6 space-y-5">
                            <div>
                                <label className="overline block mb-2">Documents requested</label>
                                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto border border-ink/10 p-3">
                                    {types.map((t) => (
                                        <label key={t.id} className="flex items-start gap-2 p-2 hover:bg-bone cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selected.includes(t.id)}
                                                onChange={() => toggle(t.id)}
                                                className="mt-0.5"
                                                data-testid={`send-link-type-${t.id}`}
                                            />
                                            <div className="text-sm">
                                                <div className="text-ink">{t.label}</div>
                                                {t.has_expiry && <div className="text-[10px] text-ink/40 uppercase tracking-wider">expiry tracked</div>}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                <p className="text-xs text-ink/50 mt-2">{selected.length} selected</p>
                            </div>
                            <div>
                                <label className="overline block mb-2">Note to employee (optional)</label>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={3}
                                    placeholder="e.g. Please ensure your driver's license is the new ECOWAS version..."
                                    className="w-full border border-ink/15 px-3 py-2 text-sm focus:border-klein focus:outline-none resize-none"
                                    data-testid="send-link-note"
                                />
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button onClick={onClose} disabled={sending} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Cancel</button>
                            <button onClick={onSend} disabled={sending || selected.length === 0} className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="send-link-submit">
                                <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send link"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function UploadDialog({ employeeId, types, onClose, onDone }) {
    const fileRef = useRef(null);
    const [docType, setDocType] = useState(types[0]?.id || "");
    const [file, setFile] = useState(null);
    const [issuedDate, setIssuedDate] = useState("");
    const [expiryDate, setExpiryDate] = useState("");
    const [annualRent, setAnnualRent] = useState("");
    const [notes, setNotes] = useState("");
    const [uploading, setUploading] = useState(false);

    const selectedType = types.find((t) => t.id === docType);
    const isRent = docType === "rent_receipt";

    const onUpload = async () => {
        if (!file) {
            toast.error("Pick a PDF first");
            return;
        }
        if (isRent) {
            if (!expiryDate) {
                toast.error("Rent receipts require an expiry date");
                return;
            }
            if (!annualRent || Number(annualRent) <= 0) {
                toast.error("Enter the annual rent amount declared on the receipt");
                return;
            }
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const paramsObj = {
                employee_id: employeeId,
                doc_type: docType,
                issued_date: issuedDate,
                expiry_date: expiryDate,
                notes: notes,
            };
            if (isRent) paramsObj.annual_rent = String(Number(annualRent));
            const params = new URLSearchParams(paramsObj);
            await api.post(`/documents?${params.toString()}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(isRent
                ? "Rent receipt uploaded — sent to Accountant for approval. The PAYE rent relief will activate once approved."
                : "Document uploaded");
            onDone();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={onClose} data-testid="emp-doc-upload-dialog">
            <div onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-md mb-16">
                <div className="px-6 py-5 border-b border-ink/10 flex items-start justify-between">
                    <div>
                        <div className="overline">Manual entry</div>
                        <h3 className="mt-1 font-display font-bold text-xl text-ink">Upload PDF on behalf</h3>
                    </div>
                    <button onClick={onClose} className="text-ink/40 hover:text-ink"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="overline block mb-2">Document type</label>
                        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none">
                            {types.map((t) => (<option key={t.id} value={t.id}>{t.label}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="overline block mb-2">PDF file (max 5 MB)</label>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-ink/15 file:text-sm file:bg-bone hover:file:bg-ink/5"
                            data-testid="emp-doc-upload-file"
                        />
                        {file && <p className="text-xs text-ink/60 mt-1">{file.name} · {formatBytes(file.size)}</p>}
                    </div>
                    {selectedType?.has_expiry && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="overline block mb-2">Issued</label>
                                <input type="date" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)} className="w-full border border-ink/15 px-3 py-2 text-sm focus:border-klein focus:outline-none" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Expires</label>
                                <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="w-full border border-ink/15 px-3 py-2 text-sm focus:border-klein focus:outline-none" />
                            </div>
                        </div>
                    )}
                    {isRent && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 rounded-sm" data-testid="emp-doc-rent-warning">
                            Rent receipts require Accountant approval before they appear in this employee&apos;s document list and trigger the PAYE rent relief.
                        </div>
                    )}
                    {isRent && (
                        <div>
                            <label className="overline block mb-2">Annual rent on receipt (₦) *</label>
                            <MoneyInput
                                value={annualRent}
                                onChange={(e) => setAnnualRent(e.target.value)}
                                placeholder="e.g. 1,200,000"
                                className="w-full border border-ink/15 px-3 py-2 text-sm focus:border-klein focus:outline-none"
                                data-testid="emp-doc-upload-annual-rent"
                            />
                            <p className="text-xs text-ink/50 mt-1">Used for PAYE tax relief (capped at ₦500,000 or 20% of rent, whichever is lower).</p>
                        </div>
                    )}
                    <div>
                        <label className="overline block mb-2">Notes (optional)</label>
                        <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-ink/15 px-3 py-2 text-sm focus:border-klein focus:outline-none" />
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-end gap-3">
                    <button onClick={onClose} disabled={uploading} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Cancel</button>
                    <button onClick={onUpload} disabled={uploading || !file} className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="emp-doc-upload-submit">
                        <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
}
